/**
 * Canonical status configuration shared by the Document Library and the home
 * Recent Documents/Scopes rows: the map from `(type, status)` → indicator
 * flavor + tooltip strings, the per-flavor visual tokens, and a `getStatusConfig`
 * lookup so callers can branch on "is there a mapping?" without reaching into
 * `STATUS_MAP` directly.
 *
 * Split out of the component file so the `.tsx` exports only a component
 * (`react-refresh/only-export-components`) and the maps stay one source of truth.
 */
import {
    AlertCircle,
    Check,
    Clock,
    Loader2,
    type LucideIcon,
} from "lucide-react";

// The kind of library item a status belongs to (the first key into STATUS_MAP).
// Declared here so this shared config carries no upward dependency on the
// document-library pages; the table's row type mirrors this union.
export type StatusItemType =
    | "folder"
    | "document"
    | "scope"
    | "comparison"
    | "template"
    | "template-render";

export type IndicatorKind = "processing" | "attention" | "success" | "failed";

export interface StatusConfig {
    indicator: IndicatorKind;
    title: string;
    description: string;
}

export const STATUS_MAP: Record<string, Record<string, StatusConfig>> = {
    scope: {
        pending: {
            indicator: "processing",
            title: "Waiting for document",
            description: "Document is still being processed",
        },
        processing: {
            indicator: "processing",
            title: "Scope in progress",
            description: "Analyzing clauses and fields",
        },
        complete: {
            indicator: "success",
            title: "Scope complete",
            description: "Analysis results are ready",
        },
        failed: {
            indicator: "failed",
            title: "Scope failed",
            description: "Analysis encountered an error",
        },
    },
    document: {
        processing: {
            indicator: "processing",
            title: "Processing document",
            description: "Text extraction and analysis in progress",
        },
        processed: {
            indicator: "success",
            title: "Document ready",
            description: "Text extraction complete",
        },
        failed: {
            indicator: "failed",
            title: "Processing failed",
            description: "Document could not be processed",
        },
    },
    template: {
        pending: {
            indicator: "processing",
            title: "Compiling",
            description: "Reading the template layout",
        },
        processing: {
            indicator: "processing",
            title: "Compiling",
            description: "Reading the template layout",
        },
        ready: {
            indicator: "success",
            title: "Ready",
            description: "Serving workflows",
        },
        failed: {
            indicator: "failed",
            title: "Compile failed",
            description: "Re-roll or replace to try again",
        },
        // `superseded` is intentionally unmapped: a retired version drops to
        // the neutral file-type glyph rather than carrying a status pill.
    },
    "template-render": {
        pending: {
            indicator: "attention",
            title: "Preparing document",
            description: "Filling the template from this conversation",
        },
        processing: {
            indicator: "attention",
            title: "Preparing document",
            description: "Filling the template from this conversation",
        },
        completed: {
            indicator: "success",
            title: "Document ready",
            description: "Your filled document is ready to open",
        },
        failed: {
            indicator: "failed",
            title: "Fill failed",
            description: "The template could not be filled",
        },
    },
};

// Indicator-flavor → visual tokens. Keeps the trigger badge, the rich-tooltip
// header icon, and the title color in lockstep so all three read as one unit.
// `badge` drives the 18px Document Library glyph; `dot` is the solid fill for
// the compact home-row corner dot. Both come from this one map so the two
// surfaces never drift in color.
export const INDICATOR_STYLES: Record<
    IndicatorKind,
    {
        badge: string;
        dot: string;
        Icon: LucideIcon;
        iconClassName?: string;
        titleClassName: string;
    }
> = {
    processing: {
        badge: "border-primary/30 bg-primary/10 text-primary animate-pulse",
        dot: "bg-primary",
        Icon: Loader2,
        iconClassName: "animate-spin",
        titleClassName: "text-primary",
    },
    attention: {
        badge: "border-amber-300/40 bg-amber-500/10 text-amber-600 dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-400",
        dot: "bg-amber-500 dark:bg-amber-400",
        Icon: Clock,
        titleClassName: "text-amber-600 dark:text-amber-400",
    },
    success: {
        badge: "border-emerald-300/40 bg-emerald-500/10 text-emerald-600 dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-400",
        dot: "bg-emerald-500 dark:bg-emerald-400",
        Icon: Check,
        titleClassName: "text-emerald-600 dark:text-emerald-400",
    },
    failed: {
        badge: "border-destructive/30 bg-destructive/10 text-destructive",
        dot: "bg-destructive",
        Icon: AlertCircle,
        titleClassName: "text-destructive",
    },
};

// Exact-key lookup (no case normalization): callers normalize before passing.
export function getStatusConfig(
    type: StatusItemType,
    status: string | null
): StatusConfig | undefined {
    if (!status) return undefined;
    return STATUS_MAP[type]?.[status];
}
